import CardFrameSurface from "../../../components/CardFrameSurface";
import { getCardLanguagePresentation } from "../../../utils/cards/languageAdaptiveCard";

const CARD_SIZE_CLASSES = {
  compact: "w-[184px] sm:w-[210px]",
  default: "w-[225px] sm:w-[270px]",
  large: "w-[250px] sm:w-[315px]",
};

const CARD_QUESTION_CLASSES = {
  compact:
    "line-clamp-[8] text-[12px] leading-tight sm:text-[13px] [overflow-wrap:anywhere]",
  default:
    "text-[15px] leading-tight sm:text-[18px] [overflow-wrap:anywhere]",
  large:
    "text-[15px] leading-tight sm:text-[18px] [overflow-wrap:anywhere]",
};

const CARD_TRANSLATION_CLASSES = {
  compact:
    "line-clamp-4 text-[7.5px] leading-snug sm:text-[8.5px] [overflow-wrap:anywhere]",
  default: "text-[8.5px] leading-snug sm:text-[10px] [overflow-wrap:anywhere]",
  large: "text-[10px] leading-snug sm:text-[11px] [overflow-wrap:anywhere]",
};

const CARD_MULTI_QUESTION_CLASSES = {
  compact:
    "line-clamp-5 text-[12px] leading-tight sm:text-[13px] [overflow-wrap:anywhere]",
  default:
    "line-clamp-7 text-[15px] leading-tight sm:text-[18px] [overflow-wrap:anywhere]",
  large:
    "line-clamp-7 text-[15px] leading-tight sm:text-[18px] [overflow-wrap:anywhere]",
};

const CARD_MULTI_TRANSLATION_CLASSES = {
  compact:
    "line-clamp-2 text-[7.5px] leading-snug sm:text-[8.5px] [overflow-wrap:anywhere]",
  default:
    "line-clamp-3 text-[8.5px] leading-snug sm:text-[10px] [overflow-wrap:anywhere]",
  large:
    "line-clamp-3 text-[10px] leading-snug sm:text-[11px] [overflow-wrap:anywhere]",
};

const CARD_SHADOW_CLASSES = {
  compact:
    "drop-shadow-[0_18px_34px_rgba(0,0,0,0.32),0_0_22px_rgba(255,255,255,0.2)]",
  default:
    "drop-shadow-[0_40px_86px_rgba(244,217,78,0.36),0_0_62px_rgba(255,255,255,0.36),0_0_30px_rgba(106,174,37,0.24)]",
  large:
    "drop-shadow-[0_40px_86px_rgba(244,217,78,0.36),0_0_62px_rgba(255,255,255,0.36),0_0_30px_rgba(106,174,37,0.24)]",
};

function RefinedCardPreview({
  result,
  languageResult,
  draftQuestion,
  toneLabel = "",
  size = "default",
  showAmbientEffects = true,
  showSparkles = true,
  editing = false,
  editedQuestion = "",
  onEditedQuestionChange,
}) {
  const languagePresentation = getCardLanguagePresentation(result);
  const previewQuestion =
    languagePresentation.question ||
    draftQuestion ||
    "What’s something you wish felt easier to ask?";

  const languageResultPresentation = Array.isArray(languageResult?.translations)
    ? getCardLanguagePresentation({
        ...result,
        ...languageResult,
        questionRefined: languagePresentation.question,
      })
    : null;
  const hasLanguageResultTranslations =
    Array.isArray(languageResult?.translations) &&
    languageResult.translations.length > 0;
  const previewTranslations =
    hasLanguageResultTranslations
      ? languageResultPresentation.translations
      : languagePresentation.translations;
  const previewTranslation =
    languageResult?.translation ||
    languageResultPresentation?.translation ||
    languagePresentation.translation ||
    "";
  const hasMultipleTranslations = previewTranslations.length === 2;
  const cardSizeClass = CARD_SIZE_CLASSES[size] || CARD_SIZE_CLASSES.default;
  const questionClass =
    (hasMultipleTranslations && CARD_MULTI_QUESTION_CLASSES[size]) ||
    CARD_QUESTION_CLASSES[size] ||
    CARD_QUESTION_CLASSES.default;
  const translationClass =
    (hasMultipleTranslations && CARD_MULTI_TRANSLATION_CLASSES[size]) ||
    CARD_TRANSLATION_CLASSES[size] ||
    CARD_TRANSLATION_CLASSES.default;
  const translationItemClass = hasMultipleTranslations
    ? `${translationClass} min-h-0 min-w-0 max-w-full shrink overflow-hidden`
    : translationClass;
  const cardShadowClass =
    CARD_SHADOW_CLASSES[size] || CARD_SHADOW_CLASSES.default;

  return (
    <div className="relative mx-auto w-fit">
      {showAmbientEffects ? (
        <>
          <div className="refined-card-glow absolute inset-[-54px] rounded-[2.5rem]" />
          <div className="refined-card-warmth absolute inset-[-78px] rounded-[3rem]" />
        </>
      ) : null}

      {showSparkles ? (
        <>
          <span className="refined-card-sparkle left-[4%] top-[16%]" />
          <span className="refined-card-sparkle refined-card-sparkle-delay right-[-2%] top-[29%]" />
          <span className="refined-card-sparkle refined-card-sparkle-late bottom-[21%] left-[7%]" />
          <span className="refined-card-sparkle refined-card-sparkle-delay bottom-[10%] right-[8%]" />
          <span className="refined-card-sparkle refined-card-sparkle-soft right-[14%] top-[4%]" />
          <span className="refined-card-sparkle refined-card-sparkle-soft refined-card-sparkle-late bottom-[2%] left-[22%]" />
        </>
      ) : null}

      {toneLabel ? (
        <p
          className="relative z-20 mb-3 text-center text-[11px] font-black uppercase tracking-[0.24em] text-white drop-shadow-[0_0_16px_rgba(255,255,255,0.28)] sm:text-xs"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {toneLabel}
        </p>
      ) : null}

      <CardFrameSurface
        className={`mx-auto ${cardSizeClass} ${cardShadowClass}`}
      >
        <div
          className={`absolute inset-x-[17%] bottom-[22%] top-[13%] flex flex-col items-center justify-center text-center ${
            hasMultipleTranslations
              ? "min-h-0 min-w-0 max-w-full gap-3 overflow-hidden"
              : "gap-3"
          }`}
        >
          {editing ? (
            <textarea
              value={editedQuestion}
              onChange={(event) =>
                onEditedQuestionChange?.(event.target.value)
              }
              aria-label="Edit card wording"
              dir="auto"
              rows={5}
              className="w-full resize-none rounded-xl border border-[#307609]/20 bg-white/75 px-2 py-2 text-center text-[15px] font-bold leading-tight text-[#2f2a16] outline-none transition focus:border-[#307609]/45 focus:ring-4 focus:ring-[#307609]/10 sm:text-[18px]"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            />
          ) : (
            <p
              className={`${questionClass} font-bold text-[#2f2a16]`}
              dir="auto"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {previewQuestion}
            </p>
          )}

          {previewTranslations.length ? (
            previewTranslations.map((translation, index) => (
              <p
                key={`${translation.languageCode || translation.language || "translation"}-${index}`}
                className={`${translationItemClass} font-medium text-[#5f5942]`}
                dir="auto"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {translation.text}
              </p>
            ))
          ) : previewTranslation ? (
            <p
              className={`${translationClass} font-medium text-[#5f5942]`}
              dir="auto"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {previewTranslation}
            </p>
          ) : null}
        </div>
      </CardFrameSurface>

      <style>{`
        .refined-card-glow {
          background:
            radial-gradient(circle at 50% 42%, rgba(255,255,255,0.74), rgba(244,217,78,0.42) 28%, rgba(244,217,78,0.16) 50%, transparent 74%),
            radial-gradient(circle at 50% 58%, rgba(106,174,37,0.28), transparent 58%);
          filter: blur(20px);
          opacity: 0.96;
          animation: refined-card-glow-pulse 4.8s ease-in-out infinite;
        }

        .refined-card-warmth {
          background:
            radial-gradient(circle at 50% 50%, rgba(244,217,78,0.22), rgba(244,217,78,0.12) 34%, transparent 68%),
            radial-gradient(circle at 50% 65%, rgba(255,255,255,0.18), transparent 60%);
          filter: blur(30px);
          opacity: 0.82;
          animation: refined-card-warmth-pulse 6.4s ease-in-out infinite;
        }

        .refined-card-sparkle {
          position: absolute;
          z-index: 10;
          width: 12px;
          height: 12px;
          background: rgba(255,255,255,0.95);
          clip-path: polygon(50% 0%, 61% 38%, 100% 50%, 61% 62%, 50% 100%, 39% 62%, 0% 50%, 39% 38%);
          filter: drop-shadow(0 0 8px rgba(244,217,78,0.9));
          animation: refined-card-sparkle-drift 3.6s ease-in-out infinite;
        }

        .refined-card-sparkle-soft {
          width: 8px;
          height: 8px;
          opacity: 0.72;
        }

        .refined-card-sparkle-delay {
          animation-delay: 0.9s;
        }

        .refined-card-sparkle-late {
          animation-delay: 1.7s;
        }

        @keyframes refined-card-glow-pulse {
          0%, 100% { transform: scale(0.95); opacity: 0.76; }
          50% { transform: scale(1.05); opacity: 1; }
        }

        @keyframes refined-card-warmth-pulse {
          0%, 100% { transform: scale(0.94); opacity: 0.54; }
          50% { transform: scale(1.08); opacity: 0.88; }
        }

        @keyframes refined-card-sparkle-drift {
          0%, 100% { transform: translate3d(0, 8px, 0) scale(0.7) rotate(0deg); opacity: 0.22; }
          45% { transform: translate3d(0, -7px, 0) scale(1.08) rotate(18deg); opacity: 0.95; }
        }

        @media (prefers-reduced-motion: reduce) {
          .refined-card-glow,
          .refined-card-warmth,
          .refined-card-sparkle {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default RefinedCardPreview;
